import { ImagePlus } from "lucide-react";
import { img, ratioOf } from "@/lib/api";

export const SLOTS = {
  full: [[0, 0, 1, 1]],
  caption: [[0, 0, 1, 0.82]],
  two: [[0, 0, 0.49, 1], [0.51, 0, 0.49, 1]],
  three: [[0, 0, 1, 0.58], [0, 0.6, 0.49, 0.4], [0.51, 0.6, 0.49, 0.4]],
  four: [[0, 0, 0.49, 0.49], [0.51, 0, 0.49, 0.49], [0, 0.51, 0.49, 0.49], [0.51, 0.51, 0.49, 0.49]],
};
export const LAYOUT_LABELS = { full: "Full bleed", caption: "Photo + caption", two: "Side by side", three: "Hero + two", four: "Grid of four" };

function Slot({ rect, photo, enhance, onClick, onDrop, testid, full }) {
  const [x, y, w, h] = rect;
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      onDragOver={onDrop ? (e) => e.preventDefault() : undefined}
      onDrop={onDrop ? (e) => { e.preventDefault(); onDrop(e.dataTransfer.getData("photo-id")); } : undefined}
      className={`absolute overflow-hidden bg-[#E9E1D2] ${onClick ? "cursor-pointer group" : ""} ${full ? "" : "rounded-[2px]"}`}
      style={{ left: `${x * 100}%`, top: `${y * 100}%`, width: `${w * 100}%`, height: `${h * 100}%` }}
    >
      {photo ? (
        <img
          src={img(enhance ? photo.url_enhanced : photo.url)}
          alt={photo.caption || photo.filename}
          draggable={false}
          className="h-full w-full object-cover"
          style={{ objectPosition: "50% 42%" }}
        />
      ) : (
        <div className="grid h-full w-full place-items-center text-[#8C837A]">
          <ImagePlus className="h-6 w-6" strokeWidth={1.3} />
        </div>
      )}
      {onClick && <div className="absolute inset-0 ring-0 ring-[#C85A32] group-hover:ring-2 transition-shadow" />}
    </div>
  );
}

export default function PageView({ page, photos, size, theme, enhance, onSlotClick, onSlotDrop, testid, small }) {
  const ratio = ratioOf(size);
  const full = page.layout === "full";
  const rects = SLOTS[page.layout] || SLOTS.caption;
  const pad = full ? 0 : small ? 7 : 6.5;
  const showCap = page.caption && !full;
  return (
    <div data-testid={testid} className={`theme-${theme} relative w-full overflow-hidden`} style={{ aspectRatio: ratio }}>
      <div className="absolute" style={{ inset: `${pad}% ${pad}% ${showCap && page.layout !== "caption" ? pad + 5 : pad}% ${pad}%` }}>
        {rects.map((r, i) => (
          <Slot
            key={i}
            rect={r}
            full={full}
            enhance={enhance}
            photo={photos[page.photo_ids?.[i]]}
            testid={testid ? `${testid}-slot-${i}` : undefined}
            onClick={onSlotClick ? () => onSlotClick(i) : undefined}
            onDrop={onSlotDrop ? (pid) => onSlotDrop(i, pid) : undefined}
          />
        ))}
        {page.layout === "caption" && page.caption && (
          <p className={`cap absolute bottom-0 left-0 right-0 flex h-[15%] items-center justify-center text-center text-[#5C5650] ${small ? "text-[6px]" : "text-sm sm:text-lg"}`}>
            {page.caption}
          </p>
        )}
      </div>
      {showCap && page.layout !== "caption" && (
        <p className={`cap absolute left-0 right-0 text-center text-[#5C5650] ${small ? "text-[6px] bottom-[2%]" : "text-xs sm:text-base bottom-[3%]"}`}>
          {page.caption}
        </p>
      )}
    </div>
  );
}

export function CoverView({ book, photos, enhance, onClick, testid, small }) {
  const p = photos[book.cover_photo_id];
  return (
    <div data-testid={testid} onClick={onClick} className={`relative w-full overflow-hidden bg-[#272422] ${onClick ? "cursor-pointer" : ""}`} style={{ aspectRatio: ratioOf(book.size) }}>
      {p && <img src={img(enhance ? p.url_enhanced : p.url)} alt="cover" className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
      <div className={`absolute inset-x-0 bottom-0 text-center text-[#F9F6F0] ${small ? "p-1.5" : "p-6 sm:p-10"}`}>
        <h2 className={`font-serif tracking-tight leading-tight ${small ? "text-[8px]" : "text-3xl sm:text-5xl"}`}>{book.title}</h2>
        {!small && book.subtitle && <p className="mt-2 font-serif italic text-base sm:text-lg opacity-90">{book.subtitle}</p>}
      </div>
    </div>
  );
}
